// src/columns.ts
var BALL_MIN = 1;
var BALL_MAX = 75;
var SUSPENSE_MS = 1300;
var CARD_SIZE = 25;
var CARD_SIDE = 5;
var FREE_INDEX = 12;
var COLUMNS = [
  { letter: "B", min: 1, max: 15, say: "B\xEA", color: "#ff4d5e" },
  { letter: "I", min: 16, max: 30, say: "I", color: "#3fa9ff" },
  { letter: "N", min: 31, max: 45, say: "Ene", color: "#2fd27a" },
  { letter: "G", min: 46, max: 60, say: "G\xEA", color: "#ffb020" },
  { letter: "O", min: 61, max: 75, say: "O", color: "#d65bff" }
];
function columnIndexOf(ball) {
  return Math.floor((ball - 1) / 15);
}
function columnOf(ball) {
  const column2 = COLUMNS[columnIndexOf(ball)];
  if (!isValidBall(ball) || !column2) {
    throw new RangeError(`Bola fora da faixa ${BALL_MIN}..${BALL_MAX}: ${ball}`);
  }
  return column2;
}
function isValidBall(ball) {
  return Number.isInteger(ball) && ball >= BALL_MIN && ball <= BALL_MAX;
}
function columnAccepts(columnIndex, ball) {
  const column2 = COLUMNS[columnIndex];
  if (!column2) return false;
  return Number.isInteger(ball) && ball >= column2.min && ball <= column2.max;
}
function pad(ball) {
  return String(ball).padStart(2, "0");
}
function ballLabel(ball) {
  return `${columnOf(ball).letter} ${pad(ball)}`;
}
function allBalls() {
  return Array.from({ length: BALL_MAX }, (_, i) => i + 1);
}
function remainingBalls(drawn) {
  const taken = new Set(drawn);
  return allBalls().filter((n) => !taken.has(n));
}
function cardColumnIndex(cardIndex) {
  return cardIndex % CARD_SIDE;
}
function cardRowIndex(cardIndex) {
  return Math.floor(cardIndex / CARD_SIDE);
}

// src/modalities.ts
var MODALITIES = [
  "HORIZONTAL",
  "VERTICAL",
  "DIAGONAL",
  "ANY_LINE",
  "FOUR_CORNERS",
  "FULL_CARD"
];
function cells(predicate) {
  const result = [];
  for (let i = 0; i < CARD_SIZE; i += 1) {
    if (predicate(Math.floor(i / CARD_SIDE), i % CARD_SIDE)) result.push(i);
  }
  return result;
}
var row = (r) => cells((a) => a === r);
var column = (c) => cells((_, b) => b === c);
var ROWS = [0, 1, 2, 3, 4].map(row);
var COLUMNS_PATTERN = [0, 1, 2, 3, 4].map(column);
var DIAGONALS = [
  cells((a, b) => a === b),
  cells((a, b) => a + b === CARD_SIDE - 1)
];
var CORNERS = [[0, 4, 20, 24]];
var FULL = [cells(() => true)];
var MODALITY_DEFINITIONS = {
  HORIZONTAL: {
    key: "HORIZONTAL",
    name: "Linha horizontal",
    patterns: ROWS,
    frames: ROWS,
    icon: row(2)
  },
  VERTICAL: {
    key: "VERTICAL",
    name: "Linha vertical",
    patterns: COLUMNS_PATTERN,
    frames: COLUMNS_PATTERN,
    icon: column(2)
  },
  DIAGONAL: {
    key: "DIAGONAL",
    name: "Diagonal",
    patterns: DIAGONALS,
    frames: DIAGONALS,
    icon: DIAGONALS[0]
  },
  ANY_LINE: {
    key: "ANY_LINE",
    name: "Qualquer linha",
    patterns: [...ROWS, ...COLUMNS_PATTERN, ...DIAGONALS],
    // Ordem do protótipo: alterna linha/coluna/diagonal para a animação não
    // parecer uma varredura.
    frames: [
      ROWS[1],
      COLUMNS_PATTERN[3],
      DIAGONALS[1],
      ROWS[4],
      COLUMNS_PATTERN[0],
      DIAGONALS[0]
    ],
    icon: cells((a, b) => a === 2 || b === 2)
  },
  FOUR_CORNERS: {
    key: "FOUR_CORNERS",
    name: "Quatro cantos",
    patterns: CORNERS,
    frames: CORNERS,
    icon: CORNERS[0]
  },
  FULL_CARD: {
    key: "FULL_CARD",
    name: "Cartela cheia",
    patterns: FULL,
    frames: FULL,
    icon: FULL[0]
  }
};
function modalityDefinition(modality) {
  const definition = MODALITY_DEFINITIONS[modality];
  if (!definition) throw new RangeError(`Modalidade desconhecida: ${modality}`);
  return definition;
}
function modalityName(modality) {
  return modalityDefinition(modality).name;
}
function patternsOf(modality) {
  return modalityDefinition(modality).patterns;
}
function patternToMask(pattern) {
  const mask = new Array(CARD_SIZE).fill(false);
  for (const index of pattern) mask[index] = true;
  return mask;
}
function usesFreeCenter(modality) {
  return patternsOf(modality).some((pattern) => pattern.includes(FREE_INDEX));
}

// src/claim.ts
function emptyCard() {
  return new Array(CARD_SIZE).fill(null);
}
function isFreeCell(index, freeCenter) {
  return freeCenter && index === FREE_INDEX;
}
function evaluateClaim(input) {
  const { drawn, modality, freeCenter } = input;
  if (input.cells.length !== CARD_SIZE) {
    throw new RangeError(`A cartela tem ${CARD_SIZE} casas; recebi ${input.cells.length}.`);
  }
  const drawnSet = new Set(drawn);
  const lastBall = drawn.length > 0 ? drawn[drawn.length - 1] : null;
  const beforeLast = new Set(drawn.slice(0, -1));
  const values = input.cells.map(
    (cell, index) => isFreeCell(index, freeCenter) ? "free" : typeof cell === "number" ? cell : null
  );
  const seen = /* @__PURE__ */ new Set();
  const bad = /* @__PURE__ */ new Set();
  values.forEach((value, index) => {
    if (typeof value !== "number") return;
    const outOfColumn = !columnAccepts(cardColumnIndex(index), value);
    if (outOfColumn || seen.has(value)) bad.add(index);
    seen.add(value);
  });
  const isMarked = (index) => {
    const value = values[index];
    if (value === "free") return true;
    return typeof value === "number" && !bad.has(index) && drawnSet.has(value);
  };
  const wasMarkedBefore = (index) => {
    const value = values[index];
    if (value === "free") return true;
    return typeof value === "number" && beforeLast.has(value);
  };
  const isFilled = (index) => values[index] !== null;
  const patterns = patternsOf(modality);
  const winningPatterns = patterns.filter((pattern) => pattern.every(isMarked)).map((p) => [...p]);
  const winningIndices = [...new Set(winningPatterns.flat())].sort((a, b) => a - b);
  const filledCount = values.filter((value) => value !== null).length;
  let closestMissing = null;
  for (const pattern of patterns) {
    if (!pattern.every(isFilled)) continue;
    const missing = pattern.filter((index) => !isMarked(index));
    if (!closestMissing || missing.length < closestMissing.length) closestMissing = missing;
  }
  const cellStatus = values.map((value, index) => {
    if (value === "free") return "FREE";
    if (value === null) return "EMPTY";
    if (bad.has(index)) return "BAD";
    return drawnSet.has(value) ? "HIT" : "MISS";
  });
  const badIndices = [...bad].sort((a, b) => a - b);
  const name = modalityName(modality);
  if (badIndices.length > 0) {
    const plural = badIndices.length > 1;
    return {
      verdict: "INVALID",
      cellStatus,
      badIndices,
      winningIndices: [],
      winningPatterns: [],
      missingIndices: [],
      missingCount: null,
      patternWasStale: false,
      filledCount,
      canConfirm: false,
      title: "Confira os n\xFAmeros",
      detail: `${badIndices.length} n\xFAmero${plural ? "s" : ""} fora da coluna ou repetido${plural ? "s" : ""} (em vermelho).`
    };
  }
  if (winningPatterns.length > 0) {
    const stale = lastBall !== null && winningPatterns.every((pattern) => pattern.every(wasMarkedBefore));
    return {
      verdict: "BINGO",
      cellStatus,
      badIndices,
      winningIndices,
      winningPatterns,
      missingIndices: [],
      missingCount: 0,
      patternWasStale: stale,
      filledCount,
      canConfirm: true,
      title: "\xC9 bingo!",
      detail: `${name} completa com as bolas sorteadas (destacada em dourado).`,
      ...stale && lastBall !== null ? {
        warning: `Aten\xE7\xE3o: esse padr\xE3o j\xE1 estava completo antes da \xFAltima bola (${ballLabel(lastBall)}). Confira a regra da casa.`
      } : {}
    };
  }
  if (closestMissing) {
    return {
      verdict: "NOT_YET",
      cellStatus,
      badIndices,
      winningIndices: [],
      winningPatterns: [],
      missingIndices: closestMissing,
      missingCount: closestMissing.length,
      patternWasStale: false,
      filledCount,
      canConfirm: false,
      title: "Ainda n\xE3o \xE9 bingo",
      detail: closestMissing.length === 1 ? "No padr\xE3o mais pr\xF3ximo falta 1 n\xFAmero:" : `No padr\xE3o mais pr\xF3ximo faltam ${closestMissing.length} n\xFAmeros:`
    };
  }
  return {
    verdict: filledCount <= 1 ? "WAITING" : "INCOMPLETE",
    cellStatus,
    badIndices,
    winningIndices: [],
    winningPatterns: [],
    missingIndices: [],
    missingCount: null,
    patternWasStale: false,
    filledCount,
    canConfirm: false,
    title: filledCount <= 1 ? "Aguardando cartela" : "Continue digitando",
    detail: filledCount <= 1 ? `Digite os n\xFAmeros para conferir \u201C${name}\u201D.` : `Nenhum padr\xE3o de \u201C${name}\u201D foi preenchido por completo ainda.`
  };
}
function missingChipLabel(cells2, index) {
  const value = cells2[index];
  const letter = COLUMNS[cardColumnIndex(index)]?.letter ?? "?";
  return typeof value === "number" ? `${letter}${String(value).padStart(2, "0")}` : letter;
}

// src/clock.ts
function formatDuration(ms) {
  const total = Math.max(0, Math.floor(ms / 1e3));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor(total % 3600 / 60);
  const seconds = total % 60;
  const pad2 = (n) => String(n).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad2(minutes)}:${pad2(seconds)}` : `${pad2(minutes)}:${pad2(seconds)}`;
}
function roundEndAt(input, now = /* @__PURE__ */ new Date()) {
  if (input.endedAt) return input.endedAt;
  const winner = input.lastWinner;
  if (winner && winner.ballsAtWin === input.ballsCount) return winner.confirmedAt;
  return now;
}
function roundDurationMs(input, now = /* @__PURE__ */ new Date()) {
  return roundEndAt(input, now).getTime() - input.startedAt.getTime();
}
function isRoundClockStopped(input, now = /* @__PURE__ */ new Date()) {
  if (input.endedAt) return true;
  const winner = input.lastWinner;
  return Boolean(winner && winner.ballsAtWin === input.ballsCount && now);
}
function gameDurationMs(input, now = /* @__PURE__ */ new Date()) {
  return (input.endedAt ?? now).getTime() - input.startedAt.getTime();
}

// src/round.ts
function ownBallsCount(progress) {
  return Math.max(0, progress.ballsCount - progress.carriedCount);
}
function roundHasActivity(progress) {
  return ownBallsCount(progress) > 0 || progress.winnersCount > 0;
}
function nextRoundNumber(current, progress) {
  return roundHasActivity(progress) ? current + 1 : current;
}
function canUndoLastBall(progress, options) {
  return !options.roundEnded && ownBallsCount(progress) > 0;
}
function isGlobeEmpty(ballsCount) {
  return ballsCount >= 75;
}

// src/settings.ts
import { z } from "zod";

// src/voice.ts
var VOICE_SOURCES = ["SYNTHESIS", "MP3", "PHONE"];
var VOICE_SOURCE_LABELS = {
  SYNTHESIS: "Voz do tel\xE3o",
  MP3: "\xC1udio gravado",
  PHONE: "Voz do celular"
};
var VOICE_SOURCE_HINTS = {
  SYNTHESIS: "A TV fala usando a voz do navegador.",
  MP3: "A TV toca os \xE1udios gravados. (em breve)",
  PHONE: "O celular do operador fala. Use quando a TV n\xE3o tiver voz."
};
var VOICE_STATUSES = ["LOCKED", "NO_PT_VOICE", "READY"];
var VOICE_TEST_TEXT = "G\xEA... 53. 5, 3.";
var VOICE_STATUS_MESSAGES = {
  LOCKED: 'Tel\xE3o mudo: toque em "Ativar som" na TV.',
  NO_PT_VOICE: 'A TV n\xE3o tem voz em portugu\xEAs: use "Narrar pelo celular".',
  READY: "Tel\xE3o com voz pronta."
};
function ballAudioKey(ball) {
  return `ball/${ball}`;
}
var AUDIO_KEYS = {
  roundStart: "event/round-start",
  claimRejected: "event/claim-rejected",
  winner: "event/winner",
  gameFinished: "event/game-finished"
};

// src/settings.ts
var DRAW_MODES = ["MANUAL", "AUTO", "GLOBE"];
var DRAW_MODE_LABELS = {
  MANUAL: "Sorteio manual",
  AUTO: "Autom\xE1tico",
  GLOBE: "Globo f\xEDsico"
};
var AUTO_INTERVAL_MIN_SECONDS = 3;
var AUTO_INTERVAL_MAX_SECONDS = 120;
var eventSettingsSchema = z.object({
  drawMode: z.enum(DRAW_MODES).default("MANUAL"),
  autoIntervalSec: z.number().int().min(AUTO_INTERVAL_MIN_SECONDS).max(AUTO_INTERVAL_MAX_SECONDS).default(8),
  /** Suspense antes de revelar a bola. Efeito SÓ VISUAL no telão. */
  suspense: z.boolean().default(true),
  /** Narrar a bola. Desligado = locutor ao vivo. */
  narration: z.boolean().default(true),
  /**
   * Qual aparelho fala. Vive no evento (e não no telão) porque é decisão de
   * quem opera: se a TV não tem voz, ele troca para o celular e o telão
   * obedece na hora, pelo WebSocket.
   */
  voiceSource: z.enum(VOICE_SOURCES).default("SYNTHESIS"),
  /** `voiceURI` escolhido no telão; vazio = voz padrão do navegador. */
  voice: z.string().default(""),
  rate: z.number().min(0.5).max(2).default(0.95),
  /** Soletrar os algarismos ("G 53… cinco, três"). */
  digits: z.boolean().default(true),
  /** Efeitos sonoros (suspense, revelação, fanfarra). */
  sfx: z.boolean().default(true),
  /**
   * Vibração no celular do operador.
   *
   * Vive no evento, como o resto da narração, porque é decisão de quem
   * conduz o jogo — e porque o aparelho pode mudar de mão no meio do
   * evento. O sistema ainda tem a última palavra: com
   * `prefers-reduced-motion`, não vibra (ver `lib/haptics.ts`).
   */
  vibration: z.boolean().default(true),
  /** Centro da cartela conta como marcado. */
  freeCenter: z.boolean().default(true)
});
var DEFAULT_EVENT_SETTINGS = eventSettingsSchema.parse({});
function parseEventSettings(value) {
  const parsed = eventSettingsSchema.safeParse(value ?? {});
  if (parsed.success) return parsed.data;
  const partial = value && typeof value === "object" ? value : {};
  const salvaged = {};
  for (const key of Object.keys(eventSettingsSchema.shape)) {
    const field = eventSettingsSchema.shape[key];
    const result = field.safeParse(partial[key]);
    if (result.success) salvaged[key] = result.data;
  }
  return eventSettingsSchema.parse(salvaged);
}
var eventThemeSchema = z.object({
  stage: z.string().optional(),
  gold: z.string().optional(),
  columns: z.object({
    B: z.string().optional(),
    I: z.string().optional(),
    N: z.string().optional(),
    G: z.string().optional(),
    O: z.string().optional()
  }).optional()
});
function parseEventTheme(value) {
  const parsed = eventThemeSchema.safeParse(value ?? {});
  return parsed.success ? parsed.data : {};
}

// src/narration.ts
function ballAnnouncement(ball, options) {
  const column2 = columnOf(ball);
  let text = `${column2.say}... ${ball}.`;
  if (options.digits && ball > 9) {
    text += ` ${String(ball).split("").join(", ")}.`;
  }
  return text;
}
function roundStartAnnouncement(input) {
  const base = `Rodada ${input.roundNumber}. Valendo ${modalityName(input.modality)}.`;
  return input.keptBalls ? `${base} Continuamos com as bolas j\xE1 sorteadas.` : base;
}
function winnerAnnouncement(name) {
  return `Bingo! Parab\xE9ns, ${name}!`;
}
var CLAIM_REJECTED_ANNOUNCEMENT = "Bingo n\xE3o confirmado. O jogo continua.";
var CLAIM_REJECTED_BANNER = {
  title: "Bingo n\xE3o confirmado",
  subtitle: "O jogo continua"
};
function gameFinishedAnnouncement(gameNumber) {
  return `Fim da partida ${gameNumber}. Obrigado a todos!`;
}

// src/plans.ts
var PLANS = ["FREE", "FAMILY", "PRO"];
var ORG_KINDS = ["PERSONAL", "ENTITY"];
var PLAN_LIMITS = {
  // Autocadastro: uma família, uma TV, um evento por vez.
  FREE: {
    label: "Gr\xE1tis",
    maxConcurrentDisplays: 1,
    maxActiveEvents: 1,
    historyRetentionDays: 30,
    photoUpload: true,
    maxPhotoBytes: 2 * 1024 * 1024,
    customBranding: false
  },
  FAMILY: {
    label: "Fam\xEDlia",
    maxConcurrentDisplays: 2,
    maxActiveEvents: 5,
    historyRetentionDays: 365,
    photoUpload: true,
    maxPhotoBytes: 5 * 1024 * 1024,
    customBranding: false
  },
  // Comunidade, igreja, clube, empresa — é o plano das organizações que já
  // existem, e por isso é o default do banco.
  PRO: {
    label: "Profissional",
    maxConcurrentDisplays: null,
    maxActiveEvents: null,
    historyRetentionDays: null,
    photoUpload: true,
    maxPhotoBytes: 10 * 1024 * 1024,
    customBranding: true
  }
};
function planLimits(plan) {
  return PLAN_LIMITS[plan] ?? PLAN_LIMITS.PRO;
}
function withinLimit(current, limit) {
  return limit === null || current < limit;
}

// src/protocol.ts
import { z as z2 } from "zod";
var ballStateSchema = z2.object({
  number: z2.number().int().min(1).max(75),
  ordinal: z2.number().int().positive(),
  drawnAt: z2.string(),
  /**
   * Instante (hora do SERVIDOR) em que a bola pode ser mostrada.
   *
   * É `drawnAt + suspense`. Absoluto, e não "faltam N ms", porque o cliente
   * pode receber isto muito depois — numa reconexão no meio do suspense, por
   * exemplo — e precisa saber se ainda falta tempo ou se já passou. O offset
   * de relógio vem do `serverTime` do `state:sync`.
   *
   * Enquanto `revealAt` está no futuro, a bola **não existe** para o público:
   * nem no painel, nem no contador, nem nas últimas bolas.
   */
  revealAt: z2.string(),
  /** Veio da rodada anterior. Nunca pode ser desfeita. */
  inherited: z2.boolean()
});
var winnerStateSchema = z2.object({
  id: z2.string(),
  name: z2.string(),
  cardNumber: z2.string().nullable(),
  /**
   * URL **assinada**, gerada na leitura e nunca persistida (decisão 5).
   * Regenerada a cada `state:sync` e `winner:confirmed`.
   */
  photoUrl: z2.string().nullable(),
  winningBallNumber: z2.number().int().nullable(),
  ballsAtWin: z2.number().int(),
  confirmedAt: z2.string()
});
var pendingClaimStateSchema = z2.object({
  id: z2.string(),
  cardNumber: z2.string().nullable(),
  ballsAtClaim: z2.number().int(),
  openedAt: z2.string()
});
var roundStateSchema = z2.object({
  id: z2.string(),
  number: z2.number().int().positive(),
  modality: z2.enum(MODALITIES),
  prizeText: z2.string().nullable(),
  freeCenter: z2.boolean(),
  carriedCount: z2.number().int(),
  startedAt: z2.string(),
  endedAt: z2.string().nullable(),
  balls: z2.array(ballStateSchema),
  winners: z2.array(winnerStateSchema),
  pendingClaim: pendingClaimStateSchema.nullable()
});
var gameStateSchema = z2.object({
  id: z2.string(),
  number: z2.number().int().positive(),
  startedAt: z2.string(),
  endedAt: z2.string().nullable(),
  currentRound: roundStateSchema.nullable()
});
var autoDrawStateSchema = z2.object({
  running: z2.boolean(),
  intervalMs: z2.number().int().positive(),
  /** Quando a próxima bola sai. O front desenha a barra a partir disto. */
  nextAt: z2.string().nullable()
});
var voiceOptionSchema = z2.object({
  voiceURI: z2.string(),
  name: z2.string(),
  lang: z2.string()
});
var displayStateSchema = z2.object({
  displaySessionId: z2.string(),
  label: z2.string().nullable(),
  audioUnlocked: z2.boolean(),
  /** LOCKED | NO_PT_VOICE | READY — cada um pede uma ação diferente. */
  voiceStatus: z2.enum(VOICE_STATUSES),
  voiceSource: z2.enum(VOICE_SOURCES),
  /** Voz em uso no telão, quando pronta. */
  voiceURI: z2.string().nullable(),
  /** Vozes do aparelho, para o /controle poder escolher. */
  voices: z2.array(voiceOptionSchema),
  lastSeenAt: z2.string().nullable()
});
var eventStateSchema = z2.object({
  event: z2.object({
    id: z2.string(),
    name: z2.string(),
    venue: z2.string().nullable(),
    theme: z2.record(z2.unknown()),
    /** URL assinada do logo, como a foto do ganhador. */
    logoUrl: z2.string().nullable()
  }),
  settings: eventSettingsSchema,
  game: gameStateSchema.nullable(),
  autoDraw: autoDrawStateSchema,
  displays: z2.array(displayStateSchema),
  /** Hora do servidor, para o cliente calcular o offset do relógio. */
  serverTime: z2.string()
});
var gameSummarySchema = z2.object({
  gameId: z2.string(),
  gameNumber: z2.number().int(),
  totalMs: z2.number().int(),
  roundsMs: z2.number().int(),
  roundsCount: z2.number().int(),
  rounds: z2.array(
    z2.object({
      number: z2.number().int(),
      modality: z2.enum(MODALITIES),
      prizeText: z2.string().nullable(),
      ballsCount: z2.number().int(),
      ownBallsCount: z2.number().int(),
      carriedCount: z2.number().int(),
      startedAt: z2.string(),
      durationMs: z2.number().int(),
      winners: z2.array(z2.object({ name: z2.string(), cardNumber: z2.string().nullable() })),
      rejectedClaims: z2.number().int()
    })
  )
});
var serverMessageSchema = z2.discriminatedUnion("type", [
  z2.object({ type: z2.literal("state:sync"), data: eventStateSchema }),
  z2.object({
    type: z2.literal("ball:drawn"),
    data: z2.object({
      ball: ballStateSchema,
      round: roundStateSchema,
      /**
       * Mesmo valor de `ball.revealAt`, repetido no topo por conveniência.
       * Até ele chegar, o cliente mostra o giro e **nada** que revele o
       * número.
       */
      revealAt: z2.string()
    })
  }),
  z2.object({
    type: z2.literal("ball:undone"),
    data: z2.object({
      number: z2.number().int().min(1).max(75),
      ordinal: z2.number().int().positive(),
      round: roundStateSchema
    })
  }),
  z2.object({
    type: z2.literal("round:started"),
    data: z2.object({
      round: roundStateSchema,
      keptBalls: z2.boolean(),
      reconfigured: z2.boolean()
    })
  }),
  z2.object({ type: z2.literal("round:updated"), data: z2.object({ round: roundStateSchema }) }),
  z2.object({
    type: z2.literal("claim:opened"),
    data: z2.object({
      claimId: z2.string(),
      cardNumber: z2.string().nullable(),
      ballsAtClaim: z2.number().int()
    })
  }),
  z2.object({
    type: z2.literal("claim:result"),
    data: z2.object({
      claimId: z2.string(),
      status: z2.enum(["SIM", "NAO", "MANUAL", "CANCELADA"]),
      /** As 25 casas digitadas, para o telão desenhar a cartela (R8). */
      cells: z2.array(z2.number().int().nullable()),
      winningIndices: z2.array(z2.number().int()),
      missingIndices: z2.array(z2.number().int()),
      patternWasStale: z2.boolean(),
      title: z2.string(),
      detail: z2.string(),
      warning: z2.string().optional(),
      round: roundStateSchema
    })
  }),
  z2.object({
    type: z2.literal("winner:confirmed"),
    data: z2.object({ winner: winnerStateSchema, round: roundStateSchema })
  }),
  z2.object({ type: z2.literal("game:finished"), data: gameSummarySchema }),
  /**
   * "Mostrar no telão": exibe o resumo de uma partida ANTIGA.
   *
   * Mensagem própria, e não `game:finished` reaproveitado, porque o telão
   * precisa distinguir "a partida acabou agora" de "estão revendo a partida
   * de ontem" — a primeira encerra o jogo em curso, a segunda é uma consulta
   * que não muda estado nenhum. Com o mesmo tipo, um telão reconectando no
   * meio da consulta pensaria que a partida atual terminou.
   */
  z2.object({
    type: z2.literal("history:shown"),
    data: z2.object({
      summary: gameSummarySchema,
      eventName: z2.string(),
      startedAt: z2.string()
    })
  }),
  /** "Voltar ao jogo": o telão volta ao painel da partida atual. */
  z2.object({ type: z2.literal("history:hidden"), data: z2.object({}) }),
  z2.object({ type: z2.literal("auto:tick"), data: autoDrawStateSchema }),
  z2.object({
    type: z2.literal("display:state"),
    data: z2.object({ displays: z2.array(displayStateSchema) })
  }),
  z2.object({ type: z2.literal("display:revoked"), data: z2.object({ reason: z2.string() }) }),
  z2.object({
    type: z2.literal("settings:updated"),
    data: z2.object({ settings: eventSettingsSchema, eventName: z2.string() })
  }),
  /** Sala anônima da solicitação de pareamento: a TV espera aqui (R10). */
  z2.object({
    type: z2.literal("pairing:approved"),
    data: z2.object({ requestId: z2.string(), eventName: z2.string() })
  }),
  /**
   * O operador pediu para repetir a última bola.
   *
   * Não muda estado nenhum: é só "cante de novo". Existe porque alguém no
   * salão não ouviu, e por isso **fala mesmo com a narração automática
   * desligada** — é pedido explícito, não narração automática.
   */
  z2.object({
    type: z2.literal("ball:repeat"),
    data: z2.object({ number: z2.number().int().min(1).max(75) })
  }),
  /** "Testar voz": o aparelho escolhido fala este texto. */
  z2.object({
    type: z2.literal("voice:test"),
    data: z2.object({ text: z2.string(), voiceURI: z2.string().nullable() })
  }),
  z2.object({
    type: z2.literal("error"),
    data: z2.object({ code: z2.string(), message: z2.string() })
  })
]);
var serverEnvelopeSchema = z2.object({
  /** Monotônico por sala. Salto = o cliente pede `resync`. */
  seq: z2.number().int().nonnegative(),
  at: z2.string(),
  payload: serverMessageSchema
});
var clientMessageSchema = z2.discriminatedUnion("type", [
  z2.object({
    type: z2.literal("hello"),
    data: z2.object({ clientKind: z2.enum(["DISPLAY", "CONTROL", "ADMIN", "PAIRING"]) })
  }),
  z2.object({ type: z2.literal("ping"), data: z2.object({}).optional() }),
  z2.object({ type: z2.literal("resync"), data: z2.object({}).optional() }),
  z2.object({
    type: z2.literal("display:status"),
    data: z2.object({
      status: z2.enum(VOICE_STATUSES),
      source: z2.enum(VOICE_SOURCES),
      voiceURI: z2.string().nullable(),
      voices: z2.array(voiceOptionSchema)
    })
  })
]);
function reconnectDelayMs(attempt) {
  return Math.min(1e4, 500 * 2 ** Math.max(0, attempt - 1));
}

// src/roles.ts
var ROLES = ["OWNER", "ADMIN", "OPERATOR"];
var RANK = { OPERATOR: 1, ADMIN: 2, OWNER: 3 };
function roleAtLeast(role, minimum) {
  return RANK[role] >= RANK[minimum];
}

// src/dto/auth.ts
import { z as z3 } from "zod";
var emailSchema = z3.string().trim().toLowerCase().email().max(254);
var passwordSchema = z3.string().min(8).max(200);
var loginBodySchema = z3.object({
  email: emailSchema,
  password: passwordSchema,
  /** Opcional: qual organização ativar. Se o usuário só tem uma, é automático. */
  organizationId: z3.string().min(1).optional()
});
var membershipSummarySchema = z3.object({
  organizationId: z3.string(),
  organizationName: z3.string(),
  organizationSlug: z3.string(),
  role: z3.enum(ROLES)
});
var userSummarySchema = z3.object({
  id: z3.string(),
  email: z3.string(),
  name: z3.string(),
  isPlatformAdmin: z3.boolean(),
  mustChangePassword: z3.boolean()
});
var sessionResponseSchema = z3.object({
  accessToken: z3.string(),
  /** Segundos de validade do access token. */
  expiresIn: z3.number().int().positive(),
  user: userSummarySchema,
  memberships: z3.array(membershipSummarySchema),
  /** `null` quando o usuário tem várias organizações e ainda não escolheu. */
  activeOrganizationId: z3.string().nullable(),
  activeRole: z3.enum(ROLES).nullable()
});
var meResponseSchema = sessionResponseSchema.omit({
  accessToken: true,
  expiresIn: true
});
var refreshBodySchema = z3.object({
  /**
   * Qual organização ativar no novo access token. O servidor SEMPRE confere a
   * membership — mandar o id de outra organização só resulta em 403.
   */
  organizationId: z3.string().min(1).optional()
});
var switchOrgBodySchema = z3.object({
  organizationId: z3.string().min(1)
});

// src/dto/display.ts
import { z as z4 } from "zod";
var PAIRING_CODE_LENGTH = 6;
var pairingCodeSchema = z4.string().trim().regex(new RegExp(`^\\d{${PAIRING_CODE_LENGTH}}$`), "O c\xF3digo tem 6 d\xEDgitos.");
var createDisplayCodeBodySchema = z4.object({
  /** Apelido para o operador saber qual telão é qual ("salão", "palco"). */
  label: z4.string().trim().min(1).max(60).optional()
});
var displayCodeResponseSchema = z4.object({
  displaySessionId: z4.string(),
  code: z4.string(),
  expiresAt: z4.string(),
  label: z4.string().nullable()
});
var pairBodySchema = z4.object({
  code: pairingCodeSchema,
  label: z4.string().trim().min(1).max(60).optional()
});
var pairResponseSchema = z4.object({
  displayToken: z4.string(),
  expiresIn: z4.number().int().positive(),
  displaySessionId: z4.string(),
  event: z4.object({
    id: z4.string(),
    name: z4.string(),
    theme: z4.record(z4.unknown())
  })
});
var renewDisplayTokenResponseSchema = z4.object({
  displayToken: z4.string(),
  expiresIn: z4.number().int().positive(),
  /** `false` = ainda longe de expirar, o token devolvido é o mesmo. */
  renewed: z4.boolean()
});
var displaySessionSummarySchema = z4.object({
  id: z4.string(),
  eventId: z4.string(),
  label: z4.string().nullable(),
  pairedAt: z4.string().nullable(),
  lastSeenAt: z4.string().nullable(),
  revokedAt: z4.string().nullable(),
  audioUnlocked: z4.boolean()
});

// src/dto/device.ts
import { z as z5 } from "zod";
var DEVICE_CODE_ALPHABET = "ABCDEFGHJKLMNPQRTUVWXY3469";
var DEVICE_CODE_LENGTH = 6;
var deviceCodeSchema = z5.string().trim().toUpperCase().regex(
  new RegExp(`^[${DEVICE_CODE_ALPHABET}]{${DEVICE_CODE_LENGTH}}$`),
  "C\xF3digo de pareamento inv\xE1lido."
);
var createDisplayRequestBodySchema = z5.object({
  /** Apelido opcional para o operador reconhecer o telão ("TV da sala"). */
  label: z5.string().trim().min(1).max(60).optional()
});
var displayRequestResponseSchema = z5.object({
  requestId: z5.string(),
  code: z5.string(),
  /**
   * Segredo que **só a TV conhece**. O código aparece no QR e em texto grande,
   * então uma foto ou uma transmissão ao vivo o expõem; sem este segredo, o
   * código sozinho não troca por token.
   */
  deviceSecret: z5.string(),
  expiresAt: z5.string(),
  /** URL completa para o QR, já montada pelo servidor. */
  pairingUrl: z5.string(),
  /** Segundos até expirar — a TV usa para se reanunciar sozinha. */
  expiresIn: z5.number().int().positive()
});
var displayRequestStatusResponseSchema = z5.object({
  status: z5.enum(["PENDING", "APPROVED", "CONSUMED", "EXPIRED"]),
  expiresAt: z5.string(),
  eventName: z5.string().nullable()
});
var approveDisplayRequestBodySchema = z5.object({
  /** Evento existente a ser exibido. Omita para usar `quickGame`. */
  eventId: z5.string().min(1).optional(),
  /**
   * "Jogo rápido": cria evento + partida + rodada com os defaults do protótipo,
   * numa transação. É o caminho de quem só quer jogar agora.
   */
  quickGame: z5.object({
    eventName: z5.string().trim().min(1).max(120).default("Bingo em fam\xEDlia")
  }).optional()
});
var approveDisplayRequestResponseSchema = z5.object({
  requestId: z5.string(),
  eventId: z5.string(),
  eventName: z5.string(),
  /** Para onde o celular deve ir em seguida. */
  controlUrl: z5.string()
});
var exchangeDisplayRequestBodySchema = z5.object({
  requestId: z5.string().min(1),
  deviceSecret: z5.string().min(1)
});
var displayAudioStatusBodySchema = z5.object({
  unlocked: z5.boolean(),
  source: z5.enum(VOICE_SOURCES),
  synthesisAvailable: z5.boolean()
});

// src/dto/commands.ts
import { z as z6 } from "zod";
var drawBallBodySchema = z6.object({
  source: z6.enum(["MANUAL", "AUTO", "GLOBE"]).default("MANUAL"),
  /** Modo globo físico: o número que saiu de verdade. */
  number: z6.number().int().min(1).max(75).optional()
});
var startRoundBodySchema = z6.object({
  modality: z6.enum(MODALITIES),
  prizeText: z6.string().trim().max(120).optional().nullable(),
  prizeId: z6.string().min(1).optional().nullable(),
  freeCenter: z6.boolean().default(true),
  /** Continuar com as bolas já sorteadas (mesmo lote) ou zerar o painel. */
  keepBalls: z6.boolean().default(false)
});
var startGameBodySchema = startRoundBodySchema.omit({ keepBalls: true });
var changeRoundBodySchema = z6.object({
  modality: z6.enum(MODALITIES),
  prizeText: z6.string().trim().max(120).optional().nullable(),
  prizeId: z6.string().min(1).optional().nullable(),
  freeCenter: z6.boolean().optional()
});
var openClaimBodySchema = z6.object({
  cardNumber: z6.string().trim().max(20).optional().nullable()
});
var cardCellsSchema = z6.array(z6.number().int().min(1).max(75).nullable()).length(CARD_SIZE);
var resolveClaimBodySchema = z6.object({
  cells: cardCellsSchema,
  decision: z6.enum(["CONFIRM", "FORCE", "REJECT", "CANCEL"]),
  cardNumber: z6.string().trim().max(20).optional().nullable()
});
var confirmWinnerBodySchema = z6.object({
  claimId: z6.string().min(1),
  name: z6.string().trim().min(1).max(120),
  cardNumber: z6.string().trim().max(20).optional().nullable(),
  /** Chave no S3 devolvida pelo upload da foto. Nunca uma URL. */
  photoKey: z6.string().trim().max(300).optional().nullable(),
  prizeId: z6.string().min(1).optional().nullable()
});
var autoDrawBodySchema = z6.object({
  running: z6.boolean(),
  intervalSec: z6.number().int().min(AUTO_INTERVAL_MIN_SECONDS).max(AUTO_INTERVAL_MAX_SECONDS).optional()
});
var updateEventSettingsBodySchema = z6.object({
  name: z6.string().trim().min(1).max(120).optional(),
  settings: z6.object({
    drawMode: z6.enum(["MANUAL", "AUTO", "GLOBE"]).optional(),
    autoIntervalSec: z6.number().int().min(AUTO_INTERVAL_MIN_SECONDS).max(AUTO_INTERVAL_MAX_SECONDS).optional(),
    suspense: z6.boolean().optional(),
    narration: z6.boolean().optional(),
    voiceSource: z6.enum(VOICE_SOURCES).optional(),
    voice: z6.string().optional(),
    rate: z6.number().min(0.5).max(2).optional(),
    digits: z6.boolean().optional(),
    sfx: z6.boolean().optional(),
    vibration: z6.boolean().optional(),
    freeCenter: z6.boolean().optional()
  }).strict().optional()
});
var createEventBodySchema = z6.object({
  name: z6.string().trim().min(1).max(120),
  venue: z6.string().trim().max(120).optional().nullable(),
  date: z6.string().datetime().optional().nullable()
});

// src/dto/history.ts
import { z as z7 } from "zod";
var GAME_STATUSES = ["EM_ANDAMENTO", "FINALIZADA"];
var historyQuerySchema = z7.object({
  eventId: z7.string().min(1).optional(),
  /** Início do período (inclusive), ISO. Filtra por `startedAt` da partida. */
  from: z7.string().datetime().optional(),
  /** Fim do período (inclusive), ISO. */
  to: z7.string().datetime().optional(),
  limit: z7.coerce.number().int().min(1).max(100).default(20),
  /**
   * Cursor opaco da página seguinte (o `id` da última partida recebida).
   *
   * Cursor e não `offset`: partida nova nasce no topo da lista o tempo todo,
   * e com offset a página 2 repetiria ou pularia linhas sempre que isso
   * acontecesse no meio da navegação.
   */
  cursor: z7.string().min(1).optional()
});
var historyRowSchema = z7.object({
  gameId: z7.string(),
  gameNumber: z7.number().int(),
  eventId: z7.string(),
  eventName: z7.string(),
  startedAt: z7.string(),
  endedAt: z7.string().nullable(),
  /** Do início até o fim — ou até agora, se a partida ainda está rolando. */
  totalMs: z7.number().int(),
  roundsCount: z7.number().int(),
  winnerNames: z7.array(z7.string()),
  status: z7.enum(GAME_STATUSES)
});
var historyListSchema = z7.object({
  games: z7.array(historyRowSchema),
  /** `null` quando acabou. */
  nextCursor: z7.string().nullable()
});
var historyEventOptionSchema = z7.object({
  id: z7.string(),
  name: z7.string()
});
var historyBallSchema = z7.object({
  number: z7.number().int().min(1).max(75),
  ordinal: z7.number().int().positive(),
  drawnAt: z7.string(),
  /** Herdada da rodada anterior: aparece esmaecida no painel. */
  inherited: z7.boolean()
});
var historyWinnerSchema = z7.object({
  id: z7.string(),
  name: z7.string(),
  cardNumber: z7.string().nullable(),
  /**
   * URL **assinada na leitura**, com validade. Nunca persistida (decisão 5):
   * foto de ganhador é dado pessoal de pessoa real.
   */
  photoUrl: z7.string().nullable(),
  winningBallNumber: z7.number().int().nullable(),
  ballsAtWin: z7.number().int(),
  confirmedAt: z7.string()
});
var historyClaimSchema = z7.object({
  id: z7.string(),
  cardNumber: z7.string().nullable(),
  status: z7.enum(["PENDENTE", "SIM", "NAO", "MANUAL", "CANCELADA"]),
  ballsAtClaim: z7.number().int(),
  patternWasStale: z7.boolean(),
  missingCount: z7.number().int().nullable(),
  openedAt: z7.string(),
  resolvedAt: z7.string().nullable()
});
var historyRoundSchema = z7.object({
  roundId: z7.string(),
  number: z7.number().int(),
  modality: z7.enum(MODALITIES),
  prizeText: z7.string().nullable(),
  startedAt: z7.string(),
  endedAt: z7.string().nullable(),
  durationMs: z7.number().int(),
  ballsCount: z7.number().int(),
  ownBallsCount: z7.number().int(),
  carriedCount: z7.number().int(),
  rejectedClaims: z7.number().int(),
  balls: z7.array(historyBallSchema),
  winners: z7.array(historyWinnerSchema),
  claims: z7.array(historyClaimSchema)
});
var historyGameSchema = z7.object({
  gameId: z7.string(),
  gameNumber: z7.number().int(),
  eventId: z7.string(),
  eventName: z7.string(),
  status: z7.enum(GAME_STATUSES),
  startedAt: z7.string(),
  endedAt: z7.string().nullable(),
  totalMs: z7.number().int(),
  roundsMs: z7.number().int(),
  roundsCount: z7.number().int(),
  rounds: z7.array(historyRoundSchema)
});

// src/dto/people.ts
import { z as z8 } from "zod";
var PASSWORD_MIN_LENGTH = 10;
var newPasswordSchema = z8.string().min(PASSWORD_MIN_LENGTH, `A senha precisa ter pelo menos ${PASSWORD_MIN_LENGTH} caracteres.`).max(200);
var createInviteBodySchema = z8.object({
  email: z8.string().trim().toLowerCase().email("Informe um e-mail v\xE1lido.").max(200),
  name: z8.string().trim().min(1).max(120).optional().nullable(),
  /** OWNER convida ADMIN e OPERATOR; ADMIN só convida OPERATOR. */
  role: z8.enum(ROLES),
  /** Designação adiantada: o convidado já entra operando estes eventos. */
  eventIds: z8.array(z8.string().min(1)).max(50).default([])
});
var inviteRowSchema = z8.object({
  id: z8.string(),
  email: z8.string(),
  name: z8.string().nullable(),
  role: z8.enum(ROLES),
  expiresAt: z8.string(),
  createdAt: z8.string(),
  eventIds: z8.array(z8.string()),
  /** Já venceu? O servidor decide — o relógio do celular não é confiável. */
  expired: z8.boolean()
});
var invitePreviewSchema = z8.object({
  id: z8.string(),
  email: z8.string(),
  role: z8.enum(ROLES),
  expiresAt: z8.string(),
  /** URL completa, pronta para copiar. */
  url: z8.string(),
  /** `https://wa.me/?text=…` com a mensagem montada. */
  whatsappUrl: z8.string()
});
var invitePublicSchema = z8.object({
  organizationName: z8.string(),
  email: z8.string(),
  name: z8.string().nullable(),
  role: z8.enum(ROLES),
  /** Já existe conta com este e-mail? Define se a tela pede senha ou login. */
  hasAccount: z8.boolean(),
  expiresAt: z8.string()
});
var acceptInviteBodySchema = z8.object({
  /** Só para quem ainda não tem conta. */
  name: z8.string().trim().min(1).max(120).optional(),
  password: newPasswordSchema.optional()
});
var resetPublicSchema = z8.object({
  email: z8.string(),
  name: z8.string().nullable(),
  organizationName: z8.string(),
  expiresAt: z8.string()
});
var acceptResetBodySchema = z8.object({ password: newPasswordSchema });
var memberRowSchema = z8.object({
  membershipId: z8.string(),
  userId: z8.string(),
  email: z8.string(),
  name: z8.string(),
  role: z8.enum(ROLES),
  createdAt: z8.string(),
  lastLoginAt: z8.string().nullable(),
  /** Eventos que este membro opera por designação explícita. */
  eventIds: z8.array(z8.string()),
  /** É o único OWNER? A tela desabilita rebaixar e remover. */
  lastOwner: z8.boolean()
});
var updateMemberBodySchema = z8.object({ role: z8.enum(ROLES) });
var updateAccountBodySchema = z8.object({
  name: z8.string().trim().min(1).max(120)
});
var changePasswordBodySchema = z8.object({
  currentPassword: z8.string().min(1).max(200),
  newPassword: newPasswordSchema
});
var setEventOperatorsBodySchema = z8.object({
  membershipIds: z8.array(z8.string().min(1)).max(100)
});
var eventOperatorsSchema = z8.object({
  /** Membros que podem ser designados (todos os da organização). */
  candidates: z8.array(
    z8.object({
      membershipId: z8.string(),
      name: z8.string(),
      email: z8.string(),
      role: z8.enum(ROLES)
    })
  ),
  assigned: z8.array(z8.string())
});
var createOrganizationBodySchema = z8.object({
  name: z8.string().trim().min(1).max(160),
  slug: z8.string().trim().toLowerCase().min(2).max(60).regex(/^[a-z0-9-]+$/, "Use apenas letras min\xFAsculas, n\xFAmeros e h\xEDfen."),
  ownerEmail: z8.string().trim().toLowerCase().email("Informe um e-mail v\xE1lido.").max(200),
  ownerName: z8.string().trim().min(1).max(120).optional().nullable()
});
var platformOrganizationSchema = z8.object({
  id: z8.string(),
  name: z8.string(),
  slug: z8.string(),
  plan: z8.enum(["FREE", "FAMILY", "PRO"]),
  kind: z8.enum(["PERSONAL", "ENTITY"]),
  memberCount: z8.number().int(),
  createdAt: z8.string()
});

// src/index.ts
var SHARED_VERSION = "0.1.0";
export {
  AUDIO_KEYS,
  AUTO_INTERVAL_MAX_SECONDS,
  AUTO_INTERVAL_MIN_SECONDS,
  BALL_MAX,
  BALL_MIN,
  CARD_SIDE,
  CARD_SIZE,
  CLAIM_REJECTED_ANNOUNCEMENT,
  CLAIM_REJECTED_BANNER,
  COLUMNS,
  COLUMNS_PATTERN,
  CORNERS,
  DEFAULT_EVENT_SETTINGS,
  DEVICE_CODE_ALPHABET,
  DEVICE_CODE_LENGTH,
  DIAGONALS,
  DRAW_MODES,
  DRAW_MODE_LABELS,
  FREE_INDEX,
  FULL,
  GAME_STATUSES,
  MODALITIES,
  MODALITY_DEFINITIONS,
  ORG_KINDS,
  PAIRING_CODE_LENGTH,
  PASSWORD_MIN_LENGTH,
  PLANS,
  PLAN_LIMITS,
  ROLES,
  ROWS,
  SHARED_VERSION,
  SUSPENSE_MS,
  VOICE_SOURCES,
  VOICE_SOURCE_HINTS,
  VOICE_SOURCE_LABELS,
  VOICE_STATUSES,
  VOICE_STATUS_MESSAGES,
  VOICE_TEST_TEXT,
  acceptInviteBodySchema,
  acceptResetBodySchema,
  allBalls,
  approveDisplayRequestBodySchema,
  approveDisplayRequestResponseSchema,
  autoDrawBodySchema,
  autoDrawStateSchema,
  ballAnnouncement,
  ballAudioKey,
  ballLabel,
  ballStateSchema,
  canUndoLastBall,
  cardCellsSchema,
  cardColumnIndex,
  cardRowIndex,
  changePasswordBodySchema,
  changeRoundBodySchema,
  clientMessageSchema,
  columnAccepts,
  columnIndexOf,
  columnOf,
  confirmWinnerBodySchema,
  createDisplayCodeBodySchema,
  createDisplayRequestBodySchema,
  createEventBodySchema,
  createInviteBodySchema,
  createOrganizationBodySchema,
  deviceCodeSchema,
  displayAudioStatusBodySchema,
  displayCodeResponseSchema,
  displayRequestResponseSchema,
  displayRequestStatusResponseSchema,
  displaySessionSummarySchema,
  displayStateSchema,
  drawBallBodySchema,
  emailSchema,
  emptyCard,
  evaluateClaim,
  eventOperatorsSchema,
  eventSettingsSchema,
  eventStateSchema,
  eventThemeSchema,
  exchangeDisplayRequestBodySchema,
  formatDuration,
  gameDurationMs,
  gameFinishedAnnouncement,
  gameStateSchema,
  gameSummarySchema,
  historyBallSchema,
  historyClaimSchema,
  historyEventOptionSchema,
  historyGameSchema,
  historyListSchema,
  historyQuerySchema,
  historyRoundSchema,
  historyRowSchema,
  historyWinnerSchema,
  invitePreviewSchema,
  invitePublicSchema,
  inviteRowSchema,
  isFreeCell,
  isGlobeEmpty,
  isRoundClockStopped,
  isValidBall,
  loginBodySchema,
  meResponseSchema,
  memberRowSchema,
  membershipSummarySchema,
  missingChipLabel,
  modalityDefinition,
  modalityName,
  newPasswordSchema,
  nextRoundNumber,
  openClaimBodySchema,
  ownBallsCount,
  pad,
  pairBodySchema,
  pairResponseSchema,
  pairingCodeSchema,
  parseEventSettings,
  parseEventTheme,
  passwordSchema,
  patternToMask,
  patternsOf,
  pendingClaimStateSchema,
  planLimits,
  platformOrganizationSchema,
  reconnectDelayMs,
  refreshBodySchema,
  remainingBalls,
  renewDisplayTokenResponseSchema,
  resetPublicSchema,
  resolveClaimBodySchema,
  roleAtLeast,
  roundDurationMs,
  roundEndAt,
  roundHasActivity,
  roundStartAnnouncement,
  roundStateSchema,
  serverEnvelopeSchema,
  serverMessageSchema,
  sessionResponseSchema,
  setEventOperatorsBodySchema,
  startGameBodySchema,
  startRoundBodySchema,
  switchOrgBodySchema,
  updateAccountBodySchema,
  updateEventSettingsBodySchema,
  updateMemberBodySchema,
  userSummarySchema,
  usesFreeCenter,
  voiceOptionSchema,
  winnerAnnouncement,
  winnerStateSchema,
  withinLimit
};
//# sourceMappingURL=index.js.map